import {
  ArrowsCounterClockwise,
  Padlock,
  Password,
} from '@remarkable/ark-icons';
import { Fieldset, RadioButtonField, Typography } from '@remarkable/ark-web';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { invariant } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import type {
  PolicyAutoLockMinutesValues,
  PolicyMinimumPincodeLengthValues,
  PolicySoftwareUpdatesValues,
} from 'src/api/endpoints/cloudEnterprisePolicyApi.types';
import {
  queryPolicyProfiles,
  useCreatePolicyProfile,
  useDefaultPolicyProfile,
  useUpdatePolicyProfile,
} from 'src/api/queries/enterprisePolicies';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { Button, Divider, NotificationBox } from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { Boundary } from 'src/components/Boundary';
import { TestId } from 'src/testids';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import { userHelpers } from 'src/utils/userHelpers';

function EnterprisePoliciesEditor() {
  const queryClient = useQueryClient();
  const createPolicyProfile = useCreatePolicyProfile();
  const updatePolicyProfile = useUpdatePolicyProfile();
  const defaultPolicyProfile = useDefaultPolicyProfile();
  const featureFlags = useFeatureFlags();
  const claims = useAccessTokenClaims();

  const normalizeSoftwareUpdateValue = (
    v: PolicySoftwareUpdatesValues | null | undefined
  ): PolicySoftwareUpdatesValues => {
    // Both missing value and empty string should resolve to "manual".
    return v || 'manual';
  };

  const canChangePolicy = defaultPolicyProfile.data
    ? userHelpers.hasPermissionUpdateProfiles(claims.data)
    : userHelpers.hasPermissionCreateProfiles(claims.data);

  const [minimumPincodeLength, setMinimumPincodeLength] =
    useState<PolicyMinimumPincodeLengthValues>(
      defaultPolicyProfile.data?.policies.minimumPincodeLength?.value ?? 0
    );
  const [autoLockMinutes, setAutoLockMinutes] =
    useState<PolicyAutoLockMinutesValues>(
      defaultPolicyProfile.data?.policies.autoLockMinutes?.value ?? 0
    );
  const [softwareUpdates, setSoftwareUpdates] =
    useState<PolicySoftwareUpdatesValues>(
      normalizeSoftwareUpdateValue(
        defaultPolicyProfile.data?.policies.softwareUpdates?.value
      )
    );

  useEffect(() => {
    setMinimumPincodeLength(
      defaultPolicyProfile.data?.policies.minimumPincodeLength?.value ?? 0
    );
    setAutoLockMinutes(
      defaultPolicyProfile.data?.policies.autoLockMinutes?.value ?? 0
    );
    setSoftwareUpdates(
      normalizeSoftwareUpdateValue(
        defaultPolicyProfile.data?.policies.softwareUpdates?.value
      )
    );
  }, [
    defaultPolicyProfile.data?.policies.autoLockMinutes?.value,
    defaultPolicyProfile.data?.policies.minimumPincodeLength?.value,
    defaultPolicyProfile.data?.policies.softwareUpdates?.value,
  ]);

  const onSubmit = async (data: {
    minimumPincodeLength: PolicyMinimumPincodeLengthValues;
    autoLockMinutes: PolicyAutoLockMinutesValues;
    softwareUpdates: PolicySoftwareUpdatesValues;
  }) => {
    if (defaultPolicyProfile.data) {
      Sentry.addBreadcrumb({
        category: 'Enterprise Policies',
        message: 'Updating default policy profile',
      });

      invariant(
        defaultPolicyProfile.data.id,
        'Default policy profile has no ID'
      );

      await updatePolicyProfile.mutateAsync(
        {
          id: defaultPolicyProfile.data.id,
          name: defaultPolicyProfile.data.name,
          description: defaultPolicyProfile.data.description ?? '',
          isDefault: defaultPolicyProfile.data.isDefault,
          policies: {
            ...defaultPolicyProfile.data.policies,
            minimumPincodeLength: {
              value: data.minimumPincodeLength,
            },
            autoLockMinutes: {
              value: data.autoLockMinutes,
            },
            softwareUpdates: {
              value: normalizeSoftwareUpdateValue(data.softwareUpdates),
            },
          },
        },
        {
          onError: (error) => {
            Sentry.captureException(error, {
              extra: {
                minimumPincodeLength: data.minimumPincodeLength,
                autoLockMinutes: data.autoLockMinutes,
                softwareUpdates: data.softwareUpdates,
              },
            });
          },
        }
      );
    } else {
      Sentry.addBreadcrumb({
        category: 'Enterprise Policies',
        message: 'Creating default policy profile',
      });

      await createPolicyProfile.mutateAsync(
        {
          body: {
            name: 'Default',
            description: 'Default policy profile',
            isDefault: true,
            policies: {
              minimumPincodeLength: {
                value: data.minimumPincodeLength,
              },
              autoLockMinutes: {
                value: data.autoLockMinutes,
              },
              softwareUpdates: {
                value: normalizeSoftwareUpdateValue(data.softwareUpdates),
              },
            },
          },
        },
        {
          onError: (error) => {
            Sentry.captureException(error, {
              extra: {
                minimumPincodeLength: data.minimumPincodeLength,
                autoLockMinutes: data.autoLockMinutes,
                softwareUpdates: data.softwareUpdates,
              },
            });
          },
        }
      );
    }

    Sentry.addBreadcrumb({
      category: 'Enterprise Policies',
      message: 'Default policy profile updated',
    });

    await queryClient.invalidateQueries({
      queryKey: queryPolicyProfiles().queryKey,
    });
  };

  return (
    <div className="flex flex-col gap-32" data-cy={TestId.FormPolicyProfile}>
      <div className="flex flex-col gap-24">
        <Typography as="h2" variant="heading-xs" className="my-0">
          <Password size={24} className="inline align-top" /> Passcode
        </Typography>
        <Typography variant="body-md">
          Require a passcode for all devices and set a minimum passcode length.
        </Typography>

        <Fieldset className="grid grid-cols-2 items-center gap-x-32 lm:flex">
          {([0, 4, 6, 8] satisfies PolicyMinimumPincodeLengthValues[]).map(
            (option) => (
              <RadioButtonField
                key={option}
                id={`minimumPincodeLength-${option}`}
                name="minimumPincodeLength"
                label={option === 0 ? 'None' : `${option} digits`}
                value={option}
                checked={minimumPincodeLength === option}
                disabled={!canChangePolicy}
                onChange={() => {
                  setMinimumPincodeLength(option);
                }}
              />
            )
          )}
        </Fieldset>
      </div>

      <Divider />

      <div className="flex flex-col gap-24">
        <Typography as="h2" variant="heading-xs" className="my-0">
          <Padlock size={24} className="inline align-top" /> Standby
        </Typography>
        <Typography variant="body-md">
          Enhance security by automatically locking inactive devices.
        </Typography>

        <Fieldset className="grid grid-cols-2 items-center gap-x-32 lm:flex">
          {([0, 5, 20, 60] satisfies PolicyAutoLockMinutesValues[]).map(
            (option) => (
              <RadioButtonField
                key={option}
                name="autoLockMinutes"
                label={option === 0 ? 'None' : `After ${option} min`}
                value={option}
                checked={autoLockMinutes === option}
                disabled={!canChangePolicy}
                onChange={() => {
                  setAutoLockMinutes(option);
                }}
              />
            )
          )}
        </Fieldset>
      </div>

      {featureFlags.EnableEnterpriseSoftwareUpdatePolicy && (
        <>
          <Divider />

          <div className="flex flex-col gap-24">
            <Typography as="h2" variant="heading-xs" className="my-0">
              <ArrowsCounterClockwise
                size={24}
                className="inline rotate-x-180 align-top"
              />{' '}
              Software updates
            </Typography>
            <Typography variant="body-md">
              Choose how updates are delivered and installed on managed devices.
            </Typography>

            <Fieldset className="flex flex-col gap-16">
              {(
                [
                  {
                    value: 'manual' as const,
                    label: 'No policy',
                    description:
                      'Users must manually download updates and restart their devices.',
                  },
                  {
                    value: 'autoInstall' as const,
                    label: 'Auto-install',
                    description:
                      'The system automatically downloads updates. Users must manually restart their devices to apply them.',
                  },
                  {
                    value: 'autoInstallForceRestart' as const,
                    label: 'Auto-install and restart',
                    description:
                      'The system automatically downloads updates and restarts devices when they are inactive.',
                  },
                ] satisfies {
                  value: PolicySoftwareUpdatesValues;
                  label: string;
                  description: string;
                }[]
              ).map((option) => (
                <div key={option.value}>
                  <RadioButtonField
                    name="softwareUpdates"
                    label={option.label}
                    value={option.value}
                    checked={softwareUpdates === option.value}
                    disabled={!canChangePolicy}
                    onChange={() => setSoftwareUpdates(option.value)}
                  />
                  <Typography variant="body-sm" className="mt-4 ml-40">
                    {option.description}
                  </Typography>
                </div>
              ))}

              <NotificationBox
                variant="info"
                title="Manual restart required for reMarkable 2"
              >
                Auto-install works on all devices, but auto-restart is currently
                not available for reMarkable 2. Any reMarkable 2 devices must be
                manually restarted to complete the update process.
              </NotificationBox>
            </Fieldset>
          </div>
        </>
      )}

      {canChangePolicy && (
        <div className="mt-16 flex flex-col items-center justify-end gap-16 ls:flex-row">
          <Button
            className="w-full ls:w-auto"
            loading={
              createPolicyProfile.isPending || updatePolicyProfile.isPending
            }
            disabled={
              createPolicyProfile.isPending || updatePolicyProfile.isPending
            }
            analytics={{
              action: 'update enterprise policies',
              location: 'enterprise policies page',
              text: 'Update policies',
            }}
            onClick={() => {
              void onSubmit({
                minimumPincodeLength,
                autoLockMinutes,
                softwareUpdates,
              });
            }}
            variant="primary"
            data-cy={TestId.ButtonSubmitPolicyProfile}
          >
            Update policies
          </Button>
        </div>
      )}

      {createPolicyProfile.isError ? (
        <NotificationBox variant="error" title="Failed to create policies">
          {createPolicyProfile.error.message}
        </NotificationBox>
      ) : updatePolicyProfile.isError ? (
        <NotificationBox
          title="Failed to update policies"
          error={updatePolicyProfile.error}
        />
      ) : null}
    </div>
  );
}

export function EnterprisePoliciesPage() {
  return (
    <>
      <AppPage.Header
        className="w-full justify-start px-16 text-left lm:px-32"
        title="Policies"
      />
      <AppPage.Content
        className="max-w-l2xl"
        data-cy={TestId.PageEnterprisePolicies}
      >
        <div className="flex flex-col gap-32">
          <Typography variant="body-md">
            These settings are automatically applied to all enrolled devices. To
            receive policy updates, devices must be connected to Wi-Fi and
            running the latest software. It can take up to an hour for settings
            to apply to recently paired devices.
          </Typography>

          <Divider />

          <Boundary errorTitle="Unable to load policy settings">
            <EnterprisePoliciesEditor />
          </Boundary>
        </div>
      </AppPage.Content>
    </>
  );
}
